// @flow
import isEqual from 'lodash/fp/isEqual';
import last from 'lodash/fp/last';
import flatMap from 'lodash/fp/flatMap';
import uniqBy from 'lodash/fp/uniqBy';
import orderBy from 'lodash/fp/orderBy';
import reverse from 'lodash/fp/reverse';
import map from 'lodash/fp/map';
import pipe from 'lodash/fp/pipe';
import sum from 'lodash/fp/sum';
import filter from 'lodash/fp/filter';
import groupBy from 'lodash/fp/groupBy';
import fromPairs from 'lodash/fp/fromPairs';
import partition from 'lodash/fp/partition';
import sortBy from 'lodash/fp/sortBy';
import uniq from 'lodash/fp/uniq';
import compact from 'lodash/fp/compact';
import intersection from 'lodash/fp/intersection';
import invariant from 'invariant';
import { Map as immutableMap } from 'immutable';

// eslint-disable-next-line camelcase
import { dontUse__asModelId, makeId, type ModelId } from './model';
import type { ROA } from './type';
import {
  isAfterOrSame,
  toDate,
  farPast,
  farFuture,
  isBeforeOrSame,
  isBefore,
  withDayOffset,
  today,
  earliest,
  latest,
  isLater,
  unixTimeMs,
  earliestToLatest,
  isAfter,
  isSameDay,
} from './date';
import type { Customer } from './customer';
import type {
  StockItemAtDate,
  StockItemInWarehouseAtDate,
} from './product-with-inventory';

import type { PageProduct, PageItem } from 'modules/page-product';
import type { Order } from 'modules/order';
// TODO: Resolve this
// eslint-disable-next-line import/no-cycle
import {
  distinctSizeNames as defaultSizeNames,
  distinctSizeNamesByPosition,
  distinctAlternateNames,
  stockItemCompoundKeyFromParts,
  type Variation,
} from 'modules/product';
import { values } from 'modules/object';

/**
 * Inventory module.
 *
 * ## Key Concepts
 *
 * ### Warehouse
 *
 * _(aka "Inventory Source")_
 *
 * A warehouse represents a collection of on-hand goods, planned future goods to
 * be manufactured, and rules for fulfillment. A warehouse is responsible for
 * fulfilling orders for goods, usually in the context of a specific geographical
 * region. Customers are generally assigned to one or more warehouses based on
 * their geographical region, so that their orders can be fulfilled by the
 * closest possible warehouse.
 *
 * ### Fulfillment
 *
 * Fulfillment refers generally to the process of upholding agreements with
 * customers regarding timely delivery of goods. These agreements are in the form
 * of orders.
 *
 * ### Stock
 *
 * Stock is the scalar number of units of a particular good in the warehouse at a
 * given point in time.
 *
 * ### Stock Shipments
 *
 * Stock shipments are shipments of goods sent from factories to arrive at a
 * warehouse. These are often planned months in advance, and are an important
 * input to allocation.
 *
 * ### Allocation
 *
 * Allocation is the aspect of fulfillment having to do with the choice of
 * particular units with which to fulfill a particular order. Given planned
 * stock and a customer order, allocation is the process of answering the
 * question, which _particular_ units from planned stock should be set aside to
 * be sent to the customer in order to fulfill the commitment of the order?
 *
 * We need to allocate the allocations quantities
 * according to allocation rules:
 *
 * 1. Regular allocations are allocated from shipments in a last-in,
 *    first-out fashion (LIFO), meaning we pull from the quantity of
 *    the latest shipment first, then from the next earlier shipment,
 *    etc.
 * 2. Backorder occurs when regular allocation results in negative
 *    quantities. Backorder allocations are allocated in a FIFO
 *    fashion.
 *
 * This is based on extant skillet logic: https://git.io/JUuGQ
 *
 * ## Strategy
 *
 * ### Incomplete Information
 *
 * We assume that we don't have complete information about all inventory. For
 * example, we don't offer any method to aggregate stock of all products. This is
 * because we don't expect to ever have inventory data for all products at the
 * same time.
 *
 * We also only consider orders from the current session. We assume that hard
 * commits of stock to orders in other sessions are updated in the underlying
 * stock quantities externally.
 *
 * ### Building Inventory Information
 *
 * Two main pieces of information make up the inventory picture. Stock shipments,
 * and allocations. The central functions for getting these pieces of information
 * into the inventory are `mergedStock` and `mergedAllocations`
 * respectively. These handle merging new information about stock and allocations
 * into the inventory while performing all necessary calculations and carrying
 * out actions proscribed by allocation rules.
 *
 * @module modules/inventory
 * @see https://elasticsuite.github.io/cookbook/inventory
 */

/**
 * Identifier for a warehouse.
 *
 * @typedef {string} WarehouseId
 */
export type WarehouseId = string;

export opaque type StockItemKey = string;

/**
 * An allocation strategy whereby stock can only be allocated up to the amount
 * that is planned to be available on a given date.
 */
export type BlockingStrategy = $ReadOnly<{|
  type: 'blocking',
|}>;

/**
 * An allocation strategy wherein there is no limit put on the amount of stock
 * that can be allocated.
 */
export type UnlimitedStrategy = $ReadOnly<{|
  type: 'unlimited',
|}>;

/**
 * An allocation strategy wherein there is no limit put on the amount of stock
 * that can be allocated, but we still indicate stock levels to the user.
 */
export type UnrestrictedStrategy = $ReadOnly<{|
  type: 'unrestricted',
|}>;

/**
 * An allocation strategy whereby stock can be allocated at amounts greater
 * than what is planned to be available on the given date, but anything beyond
 * what is planned to be available will be allocated from future stock shipments
 * and therefore shipped at some later date.
 */
export type BackorderStrategy = $ReadOnly<{|
  type: 'backorder',
|}>;

/**
 * An allocation strategy that follows the backorder strategy during quantity entry.
 * Before checkout, quantites unavailable on their current ship date are automatically
 * moved to new shipments on future dates when they are planned to be available.
 * The final state matches the blocking strategy.
 */
export type ShipmentsByAvailableStockStrategy = $ReadOnly<{|
  type: 'shipmentsByAvailableStock',
|}>;

/**
 * A rule that provides a date for comparison.
 */
export type DateWindowDateRule = $ReadOnly<{|
  type: 'date',
  date: Date,
|}>;

/**
 * A rule that provides a number of days with which a date in the future can be derived
 * for comparison.
 */
export type DateWindowDaysInFutureRule = $ReadOnly<{|
  type: 'daysInFuture',
  daysInFuture: number,
|}>;

/**
 * A rule for the `DateWindowStrategy`.
 */
export type DateWindowStrategyRule =
  | DateWindowDateRule
  | DateWindowDaysInFutureRule;

/**
 * An allocation strategy whereby stock can be allocated in an unlimited fashion as long
 * as the given date passes all rules specified. If any rules fail, then stock can only
 * be allocated in a blocking fashion.
 *
 * @todo This could be abstracted out to just a `RulesStrategy`, with a target strategy
 *   and a fallback strategy, then just a rules list.
 */
export type DateWindowStrategy = $ReadOnly<{|
  type: 'dateWindow',
  rules: $ReadOnlyArray<DateWindowStrategyRule>,
|}>;

/**
 * A strategy indicating the rules by which stock should be allocated to an order.
 */
export type AllocationStrategy =
  | BlockingStrategy
  | UnlimitedStrategy
  | BackorderStrategy
  | DateWindowStrategy
  | UnrestrictedStrategy
  | ShipmentsByAvailableStockStrategy;

/**
 * Record for a stock shipment as they come in serialized on a `StockItem`.
 */
export type SerializedItemStockShipmentRecord = $ReadOnly<{|
  key: string,
  quantity: number,
  available_on: string | null,
  release_date: Date | string | null,
  warehouse_id?: WarehouseId | null,
  catalog_key: string,
  ats_type?: 'B' | 'N' | 'L' | null,
  ats_date?: Date | string | null,
  ats_days?: string | null,
|}>;

/**
 * Record for a stock shipment from a `StockItem`.
 *
 * These are a main source of stock shipment data. Whenever products are loaded we
 * retrieve a bunch of these and stuff them into inventory.
 */
export type ItemShipmentStockRecord = $ReadOnly<{|
  key: string,
  quantity: number,
  availableOn: Date | string | null,
  releaseDate: Date | null,
  warehouseId?: WarehouseId,
  catalogKey: string,
  atsType?: 'B' | 'N' | 'L' | null,
  atsDate?: Date | string | null,
  atsDays?: string | null,
|}>;

/**
 * An allocation of some quantity of units, from some particular order
 * PageItem and scheduled to ship on some particular date.
 */
export type Allocation = $ReadOnly<{|
  pageItemId: string,
  quantity: number,
  shipOn: Date,
|}>;

/**
 * A quantity of units expected to become available on the given date.
 */
export type StockShipment = $ReadOnly<{|
  quantity: number,
  availableOn: Date,
|}>;

/**
 * A quantity of units that could not be allocated, usually due to a lack of
 * unallocated stock.
 */
export type ConstrainedAllocation = $ReadOnly<{|
  pageItemId: string,
  quantity: number,
|}>;

export type AllocationRecord = $ReadOnly<{|
  ...Allocation,
  stockItemKey: string,
  warehouseId: WarehouseId,
|}>;

export type ConstrainedAllocationRecord = $ReadOnly<{|
  ...ConstrainedAllocation,
  stockItemKey: string,
  warehouseId: string,
|}>;

/**
 * A static allocation strategy, available after resolving an `AllocationStrategy` to
 * some date.
 */
export type ResolvedAllocationStrategy =
  | 'unlimited'
  | 'unrestricted'
  | 'blocking'
  | 'backorder'
  | 'shipmentsByAvailableStock';
export type StockShipments = $ReadOnlyArray<StockShipment>;

export type StockIncreaseLedgerRowCommonFields = $ReadOnly<{|
  /**
   * The date that this row represents in the ledger. All the associated figures
   * in this row are relative to this date.
   */
  date: Date,
  /**
   * The amount of stock that is scheduled to arrive at the warehouse on this date.
   *
   * Some of this stock may already be allocated, and some of it may not.
   */
  newStock: number,
  /**
   * The amount of stock that is scheduled to have arrived by this date.
   *
   * This is basically the num of newStock on all previous dates up to and including
   * this date. Currently, this number isn't really used for anything, it's just here
   * for completeness.
   */
  baseStock: number,
  /**
   * The amount of stock that is planned to have on-hand on this date. Note that
   * this does not represent real, physical stock. This is the amount of stock that
   * the manufacturer thinks that they will most likely have ready to ship out, and
   * that has not yet been allocated. So usually this is sort of the baseStock with
   * allocations subtracted via the allocation strategy.
   *
   * Note that this can be `Infinity`. This generally represents that the manufacturer
   * thinks that on this date they can have any amount the user might order ready to
   * ship. This is usually becuase the date is really far in the future, so they
   * should be able to get anything they need from the factory before this date arrives.
   *
   * This is what we usually show to the user as the "available" amount.
   */
  plannedStock: number,
  /**
   * The increase in allocated stock since the last relevant date.
   *
   * This represents allocation requests that are pulling from the newStock that is
   * scheduled to arrive on this date. Relevant allocation requests may actually occur
   * before or after this date, depending on the allocation strategy.
   *
   * This number is generally the minimum of the sum of: 1. the quantities of all relevant
   * allocation requests and 2. the newStock on this date. This may differ depending on
   * allocation strategy.
   */
  newAllocatedStock: number,
  /**
   * The total amount of stock that has been allocated by this date.
   *
   * This can be calculated by summing the newAllocatedStock of this and all previous rows.
   */
  allocatedStock: number,
  /**
   * The maximum amount that an allocation request can be made for on this date.
   * This is generally basically the allocated stock subtracted from the base stock,
   * but it can vary a lot depending on the allocation strategy.
   *
   * This is what we usually restrict the user to selecting as a quantity to order.
   * One rule for this is that I'm pretty sure it always must be the same or greater
   * than the plannedStock on this date.
   */
  allocatableStock: number,
  allocationStrategy: ResolvedAllocationStrategy,
  /**
   * Any Allocations that reserve stock arriving on this date gets stored here.
   */
  allocations: $ReadOnlyArray<Allocation>,
  /**
   * Indicates whether the stock is released for ordering.
   */
  isReleased: boolean,
|}>;

export type StockShipmentLedgerRow = $ReadOnly<{|
  type: 'StockShipment',
  ...StockIncreaseLedgerRowCommonFields,
|}>;

export type ReleaseLedgerRow = $ReadOnly<{|
  type: 'Release',
  ...StockIncreaseLedgerRowCommonFields,
|}>;

export type AlwaysLedgerRow = $ReadOnly<{|
  type: 'Always',
  ...StockIncreaseLedgerRowCommonFields,
|}>;

/**
 * A row in the Ledger that represents some event where stock levels go up.
 */
export type StockIncreaseLedgerRow =
  | StockShipmentLedgerRow
  | ReleaseLedgerRow
  | AlwaysLedgerRow;
export type AllocationRequestLedgerRow = $ReadOnly<{|
  type: 'AllocationRequest',
  date: Date,
  quantityRequested: number,
  quantityAllocated: number,
  quantityConstrained: number,
  pageItemId: string,
  allocation: Allocation,
|}>;

export type LedgerRow = StockIncreaseLedgerRow | AllocationRequestLedgerRow;

/**
 * An inventory ledger for one stock item.
 *
 * This is used to keep track of stock levels and allocations over time. Each
 * element, or row in the ledger, represents a date on which an event that is
 * relevant to the stock levels of this stock item occurs.
 *
 * This should always be kept in date-order, earliest to latest.
 */
export type Ledger = $ReadOnlyArray<LedgerRow>;

/**
 * The result of allocation being carried out for some stock item.
 */
export type StockItemBalance = $ReadOnly<{|
  // this id should be tied to each _instance_ of a StockItemBalance, meaning
  // it should be re-generated every time anything about the StockItemBalance
  // changes. that is, literally every time a new StockItemBalance is
  // instantiated. this is intended for caching / memoization uses.
  id: ModelId,
  allocations: $ReadOnlyArray<Allocation>,
  // any allocations which could not be allocated given the strategy end up in
  // here
  constrainedAllocations: $ReadOnlyArray<ConstrainedAllocation>,
  // for each stock shipment, the quantity of units from that shipment which have
  // not yet been allocated. this can be used to derive unallocated stock on a
  // given date by summing all unallocated quantities up to that date.
  //
  // technically this field could be derived, but doing so at access time would
  // take ridiculous cycles. including it here essentially as a caching field.
  // by far the most common operation is looking up `unallocatedStockByDate`,
  // having to perform the complex calculation to derive it every time would be
  // untenable.
  unallocatedStockByDate: $ReadOnlyArray<StockShipment>,
  stockShipments: StockShipments,
  strategy: AllocationStrategy,
  releaseDate: Date | null,
  ledger: Ledger,
|}>;

export type StockShipmentsWithStrategy = {|
  +strategy: AllocationStrategy,
  +stockShipments: StockShipments,
|};

export type StockShipmentsRecord = $ReadOnly<{|
  stock: StockShipmentsWithStrategy,
  releaseDate: Date | null,
  stockItemKey: string,
  warehouseId: WarehouseId,
|}>;

export type WarehouseStockShipments = {
  +[stock_item_key: string]: StockShipmentsWithStrategy,
};

export type StockItemKeyStockShipments = {
  +[stock_item_key: string]: $ReadOnlyArray<ItemShipmentStockRecord>,
};

export type WarehouseAllocations = {
  +[stock_item_key: string]: $ReadOnlyArray<Allocation>,
};

export type StockShipmentsByWarehouse = {
  +[WarehouseId]: WarehouseStockShipments,
};

export type AllocationsByWarehouse = { +[WarehouseId]: WarehouseAllocations };

export type BalancesByWarehouse = { +[WarehouseId]: StockItemBalance };

export type Inventory = immutableMap<string, BalancesByWarehouse>;

export type SizeAndAltNames = $ReadOnly<{|
  sizeNames: Array<string>,
  altNames: Array<string>,
|}>;

/**
 * The default warehouse ID given to any stock shipments not associated with any
 * specific warehouse ID.
 */
export const DEFAULT_WAREHOUSE_ID: 'DEFAULT_WAREHOUSE_ID' =
  'DEFAULT_WAREHOUSE_ID';

export const emptyStockItemBalance: StockItemBalance = {
  id: dontUse__asModelId('emptyStockItemBalance'),
  strategy: { type: 'blocking' },
  stockShipments: [],
  allocations: [],
  constrainedAllocations: [],
  unallocatedStockByDate: [],
  releaseDate: null,
  ledger: [],
};

// utility functions

export const deserializedItemStockRecord = (
  /* eslint-disable camelcase */
  {
    key,
    quantity,
    available_on,
    release_date,
    warehouse_id,
    catalog_key,
    ats_type,
    ats_days,
    ats_date,
  }: SerializedItemStockShipmentRecord,
): ItemShipmentStockRecord => ({
  key,
  quantity: quantity ?? 0,
  catalogKey: catalog_key,
  availableOn: available_on ? toDate(available_on) : today(),
  releaseDate: release_date ? toDate(release_date) : null,
  warehouseId: warehouse_id ?? DEFAULT_WAREHOUSE_ID,
  atsType: ats_type,
  atsDays: ats_days,
  atsDate: ats_date ? toDate(ats_date) : null,
  /* eslint-enable camelcase */
});

/**
 * Compound key for `StockItem` of `Product`.
 *
 * In theory this should be unique enough to refer to globally.
 */
export const stockShipmentCompoundKey = ({
  catalogKey,
  key,
}: ItemShipmentStockRecord): string => `catalog:${catalogKey}:stockItem:${key}`;

/**
 * Shipments in ascending order by `availableOn`.
 */
export const stockShipmentsInDateOrder = (
  shipments: $ReadOnlyArray<StockShipment>,
): $ReadOnlyArray<StockShipment> =>
  orderBy([(s) => s.availableOn], 'asc', shipments);

export const totalsForStockShipments = (
  stockShipments: $ReadOnlyArray<StockShipment>,
): $ReadOnlyArray<StockShipment> =>
  stockShipmentsInDateOrder(stockShipments).reduce(
    (acc, ss, idx) => [
      ...acc,
      { ...ss, quantity: ss.quantity + (acc[idx - 1]?.quantity || 0) },
    ],
    [],
  );

export const stockShipmentsForTotals = (
  stockShipments: $ReadOnlyArray<StockShipment>,
): $ReadOnlyArray<StockShipment> =>
  stockShipmentsInDateOrder(stockShipments).reduce(
    (acc, ss, idx) => [
      ...acc,
      {
        ...ss,
        quantity: ss.quantity - (stockShipments[idx - 1]?.quantity || 0),
      },
    ],
    [],
  );

export const totalsAtMultiple = (
  multiple: number,
  stockShipments: $ReadOnlyArray<StockShipment>,
): $ReadOnlyArray<StockShipment> =>
  stockShipments.map((ss) => ({ ...ss, quantity: ss.quantity / multiple }));

export const stockShipmentsAtMultiple = (
  multiple: number,
  stockShipments: $ReadOnlyArray<StockShipment>,
): $ReadOnlyArray<StockShipment> =>
  stockShipmentsForTotals(
    totalsAtMultiple(multiple, totalsForStockShipments(stockShipments)),
  );

export const minimumOfTotalsLists = (
  a: $ReadOnlyArray<StockShipment>,
  b: $ReadOnlyArray<StockShipment>,
): $ReadOnlyArray<StockShipment> => {
  const shipmentDates = sortBy((d) => d.getTime())(
    uniqBy((d) => d.toDateString())([...a, ...b].map((ss) => ss.availableOn)),
  );

  const totalsListWithDates = (
    stockShipments: $ReadOnlyArray<StockShipment>,
    dates: Array<Date>,
  ) =>
    dates
      .map((d) =>
        stockShipments
          .filter((ss) => ss.availableOn.toDateString() === d.toDateString())
          .reduce(
            (acc, { availableOn, quantity }) => ({
              availableOn,
              quantity: acc.quantity + quantity,
            }),
            { availableOn: d, quantity: 0 },
          ),
      )
      .filter(Boolean)
      .reduce((acc, current, idx, list) => {
        const previous = list.map((s) => s.quantity).slice(0, idx);
        return [
          ...acc,
          {
            ...current,
            quantity: previous
              ? Math.max(...previous, current.quantity)
              : current.quantity,
          },
        ];
      }, []);

  const expandedA = totalsListWithDates(a, shipmentDates);

  const expandedB = totalsListWithDates(b, shipmentDates);

  const expandedShipments: $ReadOnlyArray<[StockShipment, StockShipment]> =
    shipmentDates.map((_, idx) => [expandedA[idx], expandedB[idx]]);

  return expandedShipments.map(([shipmentA, shipmentB]) => ({
    ...shipmentA,
    quantity: Math.min(shipmentA.quantity, shipmentB.quantity),
  }));
};

export const stockShipmentWithFlooredQuantity = (
  stockShipment: StockShipment,
): StockShipment => ({
  ...stockShipment,
  quantity: Math.floor(stockShipment.quantity),
});

export const minimumOfStockShipmentLists = (
  a: $ReadOnlyArray<StockShipment>,
  b: $ReadOnlyArray<StockShipment>,
): $ReadOnlyArray<StockShipment> =>
  stockShipmentsForTotals(
    minimumOfTotalsLists(
      totalsForStockShipments(a),
      totalsForStockShipments(b),
    ),
  );

export const stockItemCompoundKeyForItemShipmentStockRecord = (
  record: ItemShipmentStockRecord,
): string =>
  stockItemCompoundKeyFromParts({
    catalogKey: record.catalogKey,
    stockItemKey: record.key,
  });

/**
 */
export const stockShipmentsRecordsFromItemShipmentStockRecords =
  (
    fulfillmentStrategyForStockShipment: (ItemShipmentStockRecord) => AllocationStrategy,
    releaseDateForStockShipment: (ItemShipmentStockRecord) => Date | null,
  ): ((
    stockShipments: $ReadOnlyArray<ItemShipmentStockRecord>,
  ) => $ReadOnlyArray<StockShipmentsRecord>) =>
  (
    stockShipments: $ReadOnlyArray<ItemShipmentStockRecord>,
  ): $ReadOnlyArray<StockShipmentsRecord> => {
    const byKey = groupBy(
      stockItemCompoundKeyForItemShipmentStockRecord,
      stockShipments,
    );

    return Object.keys(byKey)
      .flatMap((stockItemKey) => {
        const byWarehouseId = groupBy(
          (r) => r.warehouseId,
          byKey[stockItemKey],
        );
        return Object.keys(byWarehouseId)
          .map((warehouseId) => [warehouseId, byWarehouseId[warehouseId]])
          .map(([warehouseId, records]) => [
            stockItemKey,
            warehouseId,
            records,
          ]);
      })
      .map(([stockItemKey, warehouseId, records]) =>
        records.reduce(
          (acc, record) => ({
            ...acc,
            stockItemKey,
            warehouseId: warehouseId || DEFAULT_WAREHOUSE_ID,
            releaseDate: releaseDateForStockShipment(record),
            stock: {
              stockShipments: [
                ...(acc?.stock?.stockShipments || []),
                {
                  quantity: record.quantity,
                  availableOn: record.availableOn
                    ? toDate(record.availableOn)
                    : farPast,
                },
              ],
              strategy: fulfillmentStrategyForStockShipment(record),
            },
          }),
          ({
            stockItemKey: 'BAD_KEY',
            warehouseId: DEFAULT_WAREHOUSE_ID,
            releaseDate: null,
            stock: {
              strategy: { type: 'blocking' },
              stockShipments: [],
            },
            ...null,
          }: StockShipmentsRecord),
        ),
      );
  };

/**
 * Binary operating combining two `StockShipmentsByWarehouse`s into one.
 */
export const stockShipmentsMerged = (
  a: StockShipmentsByWarehouse,
  b: StockShipmentsByWarehouse,
): StockShipmentsByWarehouse =>
  flatMap(
    (
      warehouseId,
    ): $ReadOnlyArray<{
      warehouseId: string,
      stockItemKey: string,
      stockShipments: StockShipmentsWithStrategy,
    }> =>
      Object.keys(b[warehouseId]).map((stockItemKey: string) => ({
        warehouseId,
        stockItemKey,
        stockShipments: b[warehouseId][stockItemKey],
      })),
    Object.keys(b),
  ).reduce(
    (acc, { warehouseId, stockItemKey, stockShipments }) => ({
      ...acc,
      [warehouseId]: {
        ...(acc[warehouseId] || { ...null }),
        [stockItemKey]: stockShipments,
      },
    }),
    a,
  );

/**
 * Binary operation combining two `AllocationsByWarehouse`s into one.
 */
export const allocationsMerged = (
  a: AllocationsByWarehouse,
  b: AllocationsByWarehouse,
): AllocationsByWarehouse =>
  flatMap(
    (
      warehouseId,
    ): $ReadOnlyArray<{
      warehouseId: string,
      stockItemKey: string,
      allocations: $ReadOnlyArray<Allocation>,
    }> =>
      Object.keys(b[warehouseId]).map((stockItemKey) => ({
        warehouseId,
        stockItemKey,
        allocations: b[warehouseId][stockItemKey],
      })),
    Object.keys(b),
  ).reduce(
    (acc, { warehouseId, stockItemKey, allocations }) => ({
      ...acc,
      [warehouseId]: {
        ...(acc[warehouseId] || { ...null }),
        [stockItemKey]: allocations,
      },
    }),
    a,
  );

/**
 * Binary operation combining two `Inventory`s into one.
 */
export const inventoriesMerged = (a: Inventory, b: Inventory): Inventory =>
  a.merge(b);

/**
 * The subset of the given `StockShipmentsByWarehouse` that, if merged into the
 * given `Inventory`, would result in some change to the values within that
 * `Inventory`.
 *
 * This should be used to get rid of records from the incoming stock that would
 * result in unnecessary updates to inventory objects, that would then trickle
 * down to unnecessary UI updates. These updates are unnecessary because all the
 * values would be exactly the same but the object references would be changed,
 * so downstream consumers of these pieces of state would be forced to reconcile
 * these superfluous state changes.
 */
export const disjunctionByStock = (
  inventory: Inventory,
  incomingStock: $ReadOnlyArray<StockShipmentsRecord>,
): $ReadOnlyArray<StockShipmentsRecord> =>
  incomingStock.filter(({ stock, stockItemKey, warehouseId }) => {
    const balance = inventory.get(stockItemKey)?.[warehouseId];

    if (!balance) return true;

    return (
      !isEqual(stock.stockShipments, balance.stockShipments) ||
      !isEqual(stock.strategy, balance.strategy)
    );
  });

/**
 * The subset of the given `AllocationsByWarehouse` that, if merged into the
 * given `Inventory`, would result in some change to the values within that
 * `Inventory`.
 *
 * This should be used to get rid of records from the incoming allocations that would
 * result in unnecessary updates to inventory objects, that would then trickle
 * down to unnecessary UI updates. These updates are unnecessary because all the
 * values would be exactly the same but the object references would be changed,
 * so downstream consumers of these pieces of state would be forced to reconcile
 * these superfluous state changes.
 */
export const disjunctionByAllocation = (
  inventory: Inventory,
  incomingAllocations: $ReadOnlyArray<AllocationRecord>,
): $ReadOnlyArray<AllocationRecord> =>
  incomingAllocations.filter(
    ({ stockItemKey, warehouseId, pageItemId, quantity, shipOn }) => {
      const balance = inventory.get(stockItemKey)?.[warehouseId];

      if (!balance) return true;

      const oldAllocation = balance.allocations.find(
        (a) => a.pageItemId === pageItemId,
      );

      if (!oldAllocation) return true;

      return (
        quantity !== oldAllocation.quantity ||
        shipOn.toDateString() !== oldAllocation.shipOn.toDateString()
      );
    },
  );

export const allocationStrategyComparisonValue = (
  strategy: AllocationStrategy,
): number => {
  switch (strategy.type) {
    case 'unlimited':
      return 0;
    case 'unrestricted':
      return 1;
    case 'backorder':
      return 2;
    case 'dateWindow':
      return 3;
    case 'blocking':
      return 4;
    case 'shipmentsByAvailableStock':
      return 5;
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (strategy.type: empty);
      throw new Error(`Unhandled AllocationStrategy type: ${strategy.type}`);
  }
};

export const resolvedAllocationStrategyComparisonValue = (
  strategy: ResolvedAllocationStrategy,
): number => {
  switch (strategy) {
    case 'unlimited':
      return 0;
    case 'unrestricted':
      return 1;
    case 'backorder':
      return 2;
    case 'blocking':
      return 4;
    case 'shipmentsByAvailableStock':
      return 5;
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (strategy.type: empty);
      throw new Error(`Unhandled AllocationStrategy type: ${strategy}`);
  }
};

export const highestPriorityAllocationStrategy = (
  strategies: $ReadOnlyArray<AllocationStrategy>,
): AllocationStrategy =>
  orderBy([allocationStrategyComparisonValue], 'desc', strategies)[0];

export const highestPriorityResolvedAllocationStrategy = (
  strategies: $ReadOnlyArray<ResolvedAllocationStrategy>,
): ResolvedAllocationStrategy =>
  orderBy([resolvedAllocationStrategyComparisonValue], 'desc', strategies)[0];

/**
 * Iteratee mapped over `Warehouse`s of `Inventory`.
 */
export const mapWarehouses =
  <T>(
    iteratee: (StockItemBalance, WarehouseId) => T,
  ): ((BalancesByWarehouse) => $ReadOnlyArray<T>) =>
  (balances: BalancesByWarehouse): $ReadOnlyArray<T> =>
    Object.keys(balances).map((id) => iteratee(balances[id], id));

/**
 * Inventory with all constrained allocations filtered out.
 */
export const withoutConstrainedAllocations = (
  inventory: Inventory,
): Inventory =>
  inventory.map((balancesByWarehouse): BalancesByWarehouse => {
    const entries = Object.keys(balancesByWarehouse).map((warehouseId) => [
      warehouseId,
      balancesByWarehouse[warehouseId],
    ]);

    if (
      entries.every(
        ([, balance]) => balance.constrainedAllocations.length === 0,
      )
    ) {
      return balancesByWarehouse;
    }

    return entries
      .map(([warehouseId, balance]) => [
        warehouseId,
        {
          ...balance,
          id: makeId(),
          constrainedAllocations: [],
        },
      ])
      .reduce(
        (acc, [warehouseId, balance]) => ({
          ...acc,
          [warehouseId]: balance,
        }),
        { ...null },
      );
  });

/**
 * The unallocated stock for the given `StockItemBalance`.
 *
 * Always returns `Infinity`.
 */
export const unlimitedUnallocated = (_balance: StockItemBalance): number =>
  Infinity;

/**
 * The unallocated stock for the given `StockItemBalance` at the given `Date`
 * according to a blocking strategy.
 *
 * Pass `excludePageItemIds` to exclude allocations from particular PageItems
 * from calculations for aggregate stock amount.
 */
export const blockingUnallocatedByDate =
  (
    date: Date,
    excludePageItemIds: $ReadOnlyArray<string>,
  ): ((StockItemBalance) => number) =>
  ({
    unallocatedStockByDate,
    allocations,
    stockShipments,
  }: StockItemBalance): number =>
    Math.max(
      // i don't _think_ we should return negative unallocated here, i believe that once
      // we've removed excluded allocations, then negative stock is no longer relevant.
      0,
      Math.min(
        // this is the maximum that can possibly be considered unallocated
        sum(
          stockShipments
            .filter((s) => isBeforeOrSame(s.availableOn, date))
            .map((s) => s.quantity),
        ),
        // this is the unallocated after accounting for allocations
        sum(
          unallocatedStockByDate
            .filter((s) => isBeforeOrSame(s.availableOn, date))
            .map((s) => s.quantity),
        ) +
          // these are the allocations for excludePageItemIds which need to be
          // put back in
          sum(
            allocations
              .filter((a) => excludePageItemIds.includes(a.pageItemId))
              .map((a) => a.quantity),
          ),
      ),
    );

/**
 * The unallocated stock for the given `StockItemBalance` at the given `Date`
 * according to the `UnrestrictedStrategy`.
 *
 * Pass `excludePageItemIds` to exclude allocations from particular PageItems
 * from calculations for aggregate stock amount.
 *
 * The `UnrestrictedStrategy` here has the exact same behavior as the
 * `BlockingStrategy`.
 */
export const unrestrictedUnallocated = blockingUnallocatedByDate;

/**
 * The unallocated stock for the given `StockItemBalance` at the given `Date`
 * according to a backorder strategy.
 *
 * Pass `excludePageItemIds` to exclude allocations from particular PageItems
 * from calculations for aggregate stock amount.
 */
export const backorderUnallocatedByDate = (
  date: Date,
  excludePageItemIds: $ReadOnlyArray<string>,
  // logic for this is exactly the same as blocking, since backorder
  // strategy stuff is only reflected in `unallocatedStockByDate` which has
  // already been resolved for us.
): ((StockItemBalance) => number) =>
  blockingUnallocatedByDate(date, excludePageItemIds);

/**
 * Predicate: Is the given `allocationDate` later than the rule date?
 */
export const dateWindowDateRulePasses = (
  { date: ruleDate }: DateWindowDateRule,
  allocationDate: Date,
): boolean => isBeforeOrSame(ruleDate, allocationDate);

export const firstPassingDateForDaysInFutureRule = ({
  daysInFuture,
}: DateWindowDaysInFutureRule): Date => withDayOffset(daysInFuture, today());

/**
 * Predicate: Is the given `allocationDate` later than today + rule days in future?
 */
export const dateWindowDaysInFutureRulePasses = (
  rule: DateWindowDaysInFutureRule,
  allocationDate: Date,
): boolean =>
  isBeforeOrSame(firstPassingDateForDaysInFutureRule(rule), allocationDate);

/**
 * Predicate: Does the given rule pass with the given `allocationDate`?
 */
export const dateWindowStrategyRulePasses =
  (allocationDate: Date): ((rule: DateWindowStrategyRule) => boolean) =>
  (rule: DateWindowStrategyRule): boolean => {
    switch (rule.type) {
      case 'date':
        return dateWindowDateRulePasses(rule, allocationDate);
      case 'daysInFuture':
        return dateWindowDaysInFutureRulePasses(rule, allocationDate);
      default:
        // eslint-disable-next-line babel/no-unused-expressions
        (rule.type: empty);
        throw new Error(
          `Unrecognized DateWindowStrategyRule type: '${rule.type}'`,
        );
    }
  };

export const dateStrategyRuleFirstPassingDate = (
  rule: DateWindowStrategyRule,
): Date => {
  switch (rule.type) {
    case 'date':
      return rule.date;
    case 'daysInFuture':
      return firstPassingDateForDaysInFutureRule(rule);
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (rule.type: empty);
      throw new Error(
        `Unrecognized DateWindowStrategyRule type: '${rule.type}'`,
      );
  }
};

export const dateWindowStrategyFirstPassingDate = (
  strategy: DateWindowStrategy,
): Date | null =>
  earliest(strategy.rules.map(dateStrategyRuleFirstPassingDate));

export const allocationStrategyFirstUnlimitedDate = (
  strategy: AllocationStrategy,
): Date | null => {
  switch (strategy.type) {
    case 'unlimited':
      return today();
    case 'unrestricted':
      return today();
    case 'dateWindow':
      return dateWindowStrategyFirstPassingDate(strategy);
    case 'blocking':
      return null;
    case 'backorder':
      return null;
    case 'shipmentsByAvailableStock':
      return null;
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (strategy.type: empty);
      throw new Error(`Unrecognized AllocationStrategy type: ${strategy.type}`);
  }
};

/**
 * Predicate: Do all the rules for the strategy pass with the given `allocationDate`?
 */
export const dateWindowRulesPass =
  (
    rules: $ReadOnlyArray<DateWindowStrategyRule>,
  ): ((allocationDate: Date) => boolean) =>
  (allocationDate: Date): boolean =>
    rules.every(dateWindowStrategyRulePasses(allocationDate));

/**
 * The unallocated stock for the given `StockItemBalance` at the given `Date`
 * according to a date-window strategy.
 *
 * If all the rules pass this is the same as the unlimited strategy, otherwise
 * it is the same as the blocking strategy.
 *
 * Pass `excludePageItemIds` to exclude allocations from particular PageItems
 * from calculations for aggregate stock amount.
 */
export const dateWindowUnallocatedByDate =
  (
    date: Date,
    { rules }: DateWindowStrategy,
    excludePageItemIds: $ReadOnlyArray<string>,
  ): ((balance: StockItemBalance) => number) =>
  (balance: StockItemBalance): number =>
    dateWindowRulesPass(rules)(date)
      ? unlimitedUnallocated(balance)
      : blockingUnallocatedByDate(date, excludePageItemIds)(balance);

/**
 * Predicate: Is the given allocation strategy unlimited on the given date?
 */
export const resolvedStrategyAtDate =
  (
    strategy: AllocationStrategy,
  ): ((date: Date) => ResolvedAllocationStrategy) =>
  (date: Date): ResolvedAllocationStrategy => {
    switch (strategy.type) {
      case 'unlimited':
        return 'unlimited';
      case 'unrestricted':
        return 'unrestricted';
      case 'blocking':
        return 'blocking';
      case 'backorder':
        return 'backorder';
      case 'shipmentsByAvailableStock':
        return 'shipmentsByAvailableStock';
      case 'dateWindow':
        return dateWindowRulesPass(strategy.rules)(date)
          ? 'unlimited'
          : 'blocking';
      default:
        // eslint-disable-next-line babel/no-unused-expressions
        (strategy.type: empty);
        throw new Error(
          `Unrecognized AllocationStrategy type: '${strategy.type}'`,
        );
    }
  };

/**
 * Predicate: Should the inventory logic not even keep track of unallocated stock?
 */
export const resolvedAllocationStrategyIsUnlimitedAllocation = (
  strategy: ResolvedAllocationStrategy,
): boolean => {
  switch (strategy) {
    case 'unlimited':
      return true;
    case 'unrestricted':
      return false;
    case 'blocking':
      return false;
    case 'backorder':
      return false;
    case 'shipmentsByAvailableStock':
      return false;
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (strategy: empty);
      throw new Error(`Unrecognized AllocationStrategy type: '${strategy}'`);
  }
};

/**
 * Predicate: Is the user able to request unlimited allocations given this
 * `ResolvedAllocationStrategy`?
 */
export const resolvedAllocationStrategyIsUnlimitedSelection = (
  strategy: ResolvedAllocationStrategy,
): boolean => {
  switch (strategy) {
    case 'unlimited':
      return true;
    case 'unrestricted':
      return true;
    case 'blocking':
      return false;
    case 'backorder':
      return false;
    case 'shipmentsByAvailableStock':
      return false;
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (strategy: empty);
      throw new Error(`Unrecognized AllocationStrategy type: '${strategy}'`);
  }
};

/**
 * Predicate: Should the inventory logic not even keep track of unallocated stock?
 */
export const allocationStrategyIsUnlimitedAllocation =
  (strategy: AllocationStrategy): ((date: Date) => boolean) =>
  (date: Date): boolean =>
    resolvedAllocationStrategyIsUnlimitedAllocation(
      resolvedStrategyAtDate(strategy)(date),
    );

/**
 * Predicate: Is the user able to request unlimited allocations given this
 * `AllocationStrategy`?
 */
export const allocationStrategyIsUnlimitedSelection =
  (strategy: AllocationStrategy): ((date: Date) => boolean) =>
  (date: Date): boolean =>
    resolvedAllocationStrategyIsUnlimitedSelection(
      resolvedStrategyAtDate(strategy)(date),
    );

/**
 * The amount of unallocated stock for the given `balance` on the given `allocationDate`.
 *
 * Pass `excludePageItemIds` to exclude allocations from particular PageItems
 * from calculations for aggregate stock amount.
 */
export const unallocatedStockAtDate =
  (
    date: Date,
    excludePageItemIds?: $ReadOnlyArray<string> = [],
  ): ((balance: StockItemBalance) => number) =>
  (balance: StockItemBalance): number => {
    const { strategy } = balance;
    switch (strategy.type) {
      case 'unlimited':
        return unlimitedUnallocated(balance);
      case 'unrestricted':
        return unrestrictedUnallocated(date, excludePageItemIds)(balance);
      case 'blocking':
        return blockingUnallocatedByDate(date, excludePageItemIds)(balance);
      case 'backorder':
        return backorderUnallocatedByDate(date, excludePageItemIds)(balance);
      // this makes sense I think, but we're still going to need a way to
      // enforce blocking at checkout
      case 'shipmentsByAvailableStock':
        return backorderUnallocatedByDate(date, excludePageItemIds)(balance);
      case 'dateWindow':
        return dateWindowUnallocatedByDate(
          date,
          strategy,
          excludePageItemIds,
        )(balance);
      default:
        // eslint-disable-next-line babel/no-unused-expressions
        (strategy.type: empty);
        throw new Error(
          `Unrecognized AllocationStrategy type: '${strategy.type}'`,
        );
    }
  };

/**
 * The amount of planned allocatable stock for the given `balance` on
 * the given `allocationDate`.
 *
 * This is the normal, regular amount of stock. It's what the manufacturer plans
 * for the user to be able to allocate.
 *
 * Pass `excludePageItemIds` to exclude allocations from particular PageItems
 * from calculations for aggregate stock amount.
 */
export const plannedAllocatableStockAtDate =
  (
    date: Date,
    excludePageItemIds?: $ReadOnlyArray<string> = [],
  ): ((balance: StockItemBalance) => number) =>
  (balance: StockItemBalance): number => {
    const { strategy } = balance;
    switch (strategy.type) {
      case 'unlimited':
        return unlimitedUnallocated(balance);
      case 'unrestricted':
        return blockingUnallocatedByDate(date, excludePageItemIds)(balance);
      case 'blocking':
        return blockingUnallocatedByDate(date, excludePageItemIds)(balance);
      case 'backorder':
        return blockingUnallocatedByDate(date, excludePageItemIds)(balance);
      case 'shipmentsByAvailableStock':
        return blockingUnallocatedByDate(date, excludePageItemIds)(balance);
      case 'dateWindow':
        return dateWindowUnallocatedByDate(
          date,
          strategy,
          excludePageItemIds,
        )(balance);
      default:
        // eslint-disable-next-line babel/no-unused-expressions
        (strategy.type: empty);
        throw new Error(
          `Unrecognized AllocationStrategy type: '${strategy.type}'`,
        );
    }
  };

/**
 * Predicate: Does the given balance have any stock at any date?
 */
export const balanceHasSomeStock = (balance: StockItemBalance): boolean =>
  sum(balance.stockShipments.map((ss) => ss.quantity)) > 0;

/**
 * Predicate: Is the amount of unallocated stock in the given StockItemBalance at
 * the given Date greater than zero?
 */
export const balanceHasSomeUnallocatedStockAtDate = (
  balance: StockItemBalance,
  date: Date,
  excludePageItemIds?: $ReadOnlyArray<string> = [],
): boolean =>
  sum([
    unallocatedStockAtDate(date)(balance),
    ...balance.allocations
      .filter((a) => excludePageItemIds.includes(a.pageItemId))
      .map((a) => a.quantity),
  ]) > 0;

/**
 * The earliest `Date` for the given `StockShipment`s for which `quantity > 0`.
 *
 * Returns `null` if no such `Date` is found (empty list or all 0).
 */
export const earliestQuantityAvailableDateInStockShipments = (
  stockShipments: $ReadOnlyArray<StockShipment>,
): Date | null =>
  pipe(
    filter((shipment) => shipment.quantity > 0),
    map(({ availableOn }) => availableOn),
    earliest,
    (date) => (date ? latest([date, today()]) : null),
  )(stockShipments);

/**
 * The total of all quantities in `unallocatedStockByDate` of the given `StockItemBalance`.
 *
 * Optionally exclude allocations associated with certain PageItems from consideration by
 * specifying `excludePageItemIds`.
 */
export const totalUnallocated = (
  { unallocatedStockByDate, allocations }: StockItemBalance,
  excludePageItemIds?: $ReadOnlyArray<string> = [],
): number =>
  sum([
    ...unallocatedStockByDate.map((q) => q.quantity),
    ...allocations
      .filter((a) => excludePageItemIds.includes(a.pageItemId))
      .map((a) => a.quantity),
  ]);

/**
 * The quantity of units that can theoretically be allocated by the user on the
 * given `Date` for the given `StockItemBalance`.
 *
 * Optionally exclude allocations associated with certain PageItems from consideration by
 * specifying `excludePageItemIds`.
 */
export const allocatableForStockItemAtDate =
  (
    allocationDate: Date,
    excludePageItemIds?: $ReadOnlyArray<string> = [],
  ): ((balance: StockItemBalance) => number) =>
  (balance: StockItemBalance): number => {
    const resolvedStrategy = resolvedStrategyAtDate(balance.strategy)(
      allocationDate,
    );
    switch (resolvedStrategy) {
      case 'unlimited':
        return Infinity;
      case 'unrestricted':
        return Infinity;
      case 'backorder':
        return totalUnallocated(balance, excludePageItemIds);
      case 'shipmentsByAvailableStock':
        return totalUnallocated(balance, excludePageItemIds);
      case 'blocking':
        return blockingUnallocatedByDate(
          allocationDate,
          excludePageItemIds,
        )(balance);
      default:
        // eslint-disable-next-line babel/no-unused-expressions
        (resolvedStrategy: empty);
        throw new Error(`Unhandled ResolvedStrategy: ${resolvedStrategy}`);
    }
  };

export const hasFutureUnallocated =
  (balance: StockItemBalance): ((date: Date) => boolean) =>
  (date: Date) =>
    sum(
      balance.stockShipments
        .filter(({ availableOn }) => isLater(availableOn, date))
        .map(({ quantity }) => quantity),
    ) > 0;

/**
 * The given `StockItemBalance` with the given `Allocation` appended, along
 * with an optional corresponding `ConstrainedAllocation`.
 */
export const balanceWithAllocation = (
  balance: StockItemBalance,
  allocation: Allocation,
  constrainedAllocation?: ConstrainedAllocation | null,
): StockItemBalance => ({
  ...balance,
  allocations: [...balance.allocations, allocation],
  constrainedAllocations: constrainedAllocation
    ? [...balance.constrainedAllocations, constrainedAllocation]
    : balance.constrainedAllocations,
});

/**
 * The result of subtracting the given `Allocation` from the given list of
 * `StockShipment`s.
 *
 * The quantities of the `StockShipment`s are subtracted from by the amount
 * of the `Allocation` quantity in order of first to last (in whatever order
 * given). The amount to allocate is kept track of, and is returned as the
 * `remainder`.
 */
export const unallocatedStockByDateMinusAllocation = (
  unallocatedStockByDate: $ReadOnlyArray<StockShipment>,
  allocation: Allocation,
): {|
  +unallocatedStockByDate: $ReadOnlyArray<StockShipment>,
  +remainder: number,
|} =>
  unallocatedStockByDate.reduce(
    (acc, unallocatedStock) => {
      if (isBefore(allocation.shipOn, unallocatedStock.availableOn)) {
        return {
          unallocatedStockByDate: [
            ...acc.unallocatedStockByDate,
            unallocatedStock,
          ],
          remainder: acc.remainder,
        };
      }

      const toAllocate = Math.min(acc.remainder, unallocatedStock.quantity);

      return {
        ...acc,
        unallocatedStockByDate: [
          ...acc.unallocatedStockByDate,
          {
            quantity: unallocatedStock.quantity - toAllocate,
            availableOn: unallocatedStock.availableOn,
          },
        ],
        remainder: acc.remainder - toAllocate,
      };
    },
    ({
      unallocatedStockByDate: [],
      remainder: allocation.quantity,
    }: {|
      +unallocatedStockByDate: $ReadOnlyArray<StockShipment>,
      +remainder: number,
    |}),
  );

// Strategy allocation resolvers
//
// These are the key functions that actually implement allocation for the various
// `AllocationStrategy` types. They each take a `StockItemBalance` and an `Allocation`.

/**
 * The given `StockItemBalance` with the given `Allocation` appended, and all
 * allocation rules associated with the `UnlimitedStrategy` resolved.
 *
 * Unlimited doesn't really do anything, so we're just appending the new Allocation.
 */
export const unlimitedResolvedBalanceForAllocation = (
  balance: StockItemBalance,
  allocation: Allocation,
): StockItemBalance => ({
  ...balance,
  allocations: [...balance.allocations, allocation],
});

/**
 * The given `StockItemBalance` with the given `Allocation` appended, and all
 * allocation rules associated with the `BlockingStrategy` resolved.
 *
 * For `BlockingStrategy` we need to allocate unallocated stock from `StockShipment`s
 * where their `availableOn` is earlier than or equal to the `Allocation`'s `shipOn`
 * in LIFO order. If the subset of `StockShipment`s for which the date restriction
 * is met does not have enough unallocated stock, the remainder becomes a new
 * `ConstrainedAllocation`.
 */
export const blockingResolvedBalanceForAllocation = (
  balance: StockItemBalance,
  allocation: Allocation,
): StockItemBalance => {
  const difference = unallocatedStockByDateMinusAllocation(
    reverse(balance.unallocatedStockByDate),
    allocation,
  );

  return balanceWithAllocation(
    {
      ...balance,
      unallocatedStockByDate: reverse(difference.unallocatedStockByDate),
    },
    {
      ...allocation,
      quantity: allocation.quantity - difference.remainder,
    },
    difference.remainder > 0
      ? {
          pageItemId: allocation.pageItemId,
          quantity: difference.remainder,
        }
      : null,
  );
};

/**
 * The given `StockItemBalance` with the given `Allocation` appended, and all
 * allocation rules associated with the `UnrestrictedStrategy` resolved.
 *
 * This is exactly the same as the `BlockingStrategy` except that we never
 * adjust `allocations` or create `constrainedAllocations`.
 */
export const unrestrictedResolvedBalanceForAllocation = (
  balance: StockItemBalance,
  allocation: Allocation,
): StockItemBalance => {
  const difference = unallocatedStockByDateMinusAllocation(
    reverse(balance.unallocatedStockByDate),
    allocation,
  );

  return balanceWithAllocation(
    {
      ...balance,
      unallocatedStockByDate: reverse(difference.unallocatedStockByDate),
    },
    allocation,
  );
};

/**
 * The given `StockItemBalance` with the given `Allocation` appended, and all
 * allocation rules associated with the `BackorderStrategy` resolved.
 *
 * For `BackorderStrategy` we need to allocate unallocated stock from `StockShipment`s
 * where their `availableOn` is earlier than or equal to the `Allocation`'s `shipOn`
 * in LIFO order. If the subset of `StockShipment`s for which the date restriction
 * is met does not have enough unallocated stock, the remainder is allocated from
 * _future_ `StockShipment`s in FIFO order. If there is still any quantity to
 * allocate, it becomes a `ConstrainedAllocation`.
 */
export const backorderResolvedBalanceForAllocation = (
  balance: StockItemBalance,
  allocation: Allocation,
): StockItemBalance => {
  // LIFO
  const lifoDifference = unallocatedStockByDateMinusAllocation(
    reverse(balance.unallocatedStockByDate),
    allocation,
  );

  // FIFO
  const difference = unallocatedStockByDateMinusAllocation(
    reverse(lifoDifference.unallocatedStockByDate),
    { ...allocation, quantity: lifoDifference.remainder, shipOn: farFuture },
  );

  return balanceWithAllocation(
    { ...balance, unallocatedStockByDate: difference.unallocatedStockByDate },
    {
      ...allocation,
      quantity: allocation.quantity - difference.remainder,
    },
    difference.remainder > 0
      ? {
          pageItemId: allocation.pageItemId,
          quantity: difference.remainder,
        }
      : null,
  );
};

/**
 * The given `StockItemBalance` with the given `Allocation` appended, and all
 * allocation rules associated with the `DateWindowStrategy` resolved.
 *
 * For `DateWindowStrategy`, we check whether all rules pass. If they do, we
 * use the unlimited strategy. If they don't, we use the blocking strategy.
 */
export const dateWindowResolvedBalanceForAllocation = (
  balance: StockItemBalance,
  allocation: Allocation,
): StockItemBalance =>
  allocationStrategyIsUnlimitedAllocation(balance.strategy)(allocation.shipOn)
    ? unlimitedResolvedBalanceForAllocation(balance, allocation)
    : blockingResolvedBalanceForAllocation(balance, allocation);

export const resolvedBalanceForAllocation = (
  balance: StockItemBalance,
  allocation: Allocation,
): StockItemBalance => {
  const { strategy } = balance;
  switch (strategy.type) {
    case 'unlimited':
      return unlimitedResolvedBalanceForAllocation(balance, allocation);
    case 'unrestricted':
      return unrestrictedResolvedBalanceForAllocation(balance, allocation);
    case 'blocking':
      return blockingResolvedBalanceForAllocation(balance, allocation);
    case 'backorder':
      return backorderResolvedBalanceForAllocation(balance, allocation);
    case 'shipmentsByAvailableStock':
      return backorderResolvedBalanceForAllocation(balance, allocation);
    case 'dateWindow':
      return dateWindowResolvedBalanceForAllocation(balance, allocation);
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (strategy.type: empty);
      throw new Error(`Unhandled AllocationStrategy type: ${strategy.type}`);
  }
};

/**
 * The list of dates upon which relevant changes to stock occur for a balance with the
 * unlimited strategy.
 */
export const unlimitedRelevantDates = (
  balance: StockItemBalance,
  _strategy: AllocationStrategy,
): $ReadOnlyArray<Date> => [balance.releaseDate].filter(Boolean);

/**
 * The list of dates upon which relevant changes to stock occur for a balance with the
 * blocking strategy, in earliest-to-latest order.
 */
export const blockingRelevantDates = (
  { releaseDate, stockShipments }: StockItemBalance,
  _strategy: AllocationStrategy,
): $ReadOnlyArray<Date> =>
  earliestToLatest([
    ...(releaseDate ? [releaseDate] : []),
    ...stockShipments
      .map((ss) => ss.availableOn)
      .filter((d) => isAfter(d, releaseDate || farPast)),
  ]);

/**
 * The list of dates upon which relevant changes to stock occur for a balance with the
 * backorder strategy.
 */
export const backorderRelevantDates = blockingRelevantDates;

/**
 * The list of dates upon which relevant changes to stock occur for a balance with the
 * dateWindow strategy.
 */
export const dateWindowRelevantDates = (
  balance: StockItemBalance,
  strategy: DateWindowStrategy,
): $ReadOnlyArray<Date> => {
  const firstUnlimited = dateWindowStrategyFirstPassingDate(strategy);

  return [
    ...blockingRelevantDates(balance, strategy).filter((d) =>
      isBefore(d, firstUnlimited || farFuture),
    ),
    ...(firstUnlimited ? [firstUnlimited] : []),
  ];
};

/**
 * The list of dates upon which relevant changes to stock occur for a balance with the
 * unrestricted strategy.
 */
export const unrestrictedRelevantDates = blockingRelevantDates;

/**
 * The list of dates upon which relevant changes to stock occur for a balance.
 */
export const relevantDates = (
  balance: StockItemBalance,
): $ReadOnlyArray<Date> => {
  const allocationStrategy = balance.strategy;
  switch (allocationStrategy.type) {
    case 'unlimited':
      return unlimitedRelevantDates(balance, allocationStrategy);
    case 'unrestricted':
      return unrestrictedRelevantDates(balance, allocationStrategy);
    case 'blocking':
      return blockingRelevantDates(balance, allocationStrategy);
    case 'backorder':
      return backorderRelevantDates(balance, allocationStrategy);
    case 'shipmentsByAvailableStock':
      return backorderRelevantDates(balance, allocationStrategy);
    case 'dateWindow':
      return dateWindowRelevantDates(balance, allocationStrategy);
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (allocationStrategy.type: empty);
      throw new Error(
        `Unhandled AllocationStrategy type: ${allocationStrategy.type}`,
      );
  }
};

/**
 * The Ledger for a StockItemBalance of the unlimited AllocationStrategy.
 *
 * The unlimited ledger is special because it can regularly have no relevant
 * stock increase dates. In this scenario, a special stock increase row of type
 * 'Always' is inserted at the start of the ledger, that has a date of farPast
 * and an AllocatableStock of Infinity.
 *
 * The only relevant date for the unlimited ledger is the release date. If an
 * allocation request is scheduled for after the release date, it is fulfilled.
 * Otherwise, it is constrained.
 */
export const unlimitedLedger = (
  { releaseDate, allocations }: StockItemBalance,
  _strategy: AllocationStrategy,
): Ledger => {
  const totalAllocated = sum(allocations.map((a) => a.quantity));

  if (releaseDate) {
    const [before, after] = partition((a) => isBefore(a.shipOn, releaseDate))(
      allocations,
    );

    return [
      ...before.map((a) => ({
        type: 'AllocationRequest',
        date: a.shipOn,
        quantityRequested: a.quantity,
        quantityAllocated: 0,
        quantityConstrained: a.quantity,
        pageItemId: a.pageItemId,
        allocation: a,
      })),
      {
        type: 'Release',
        date: releaseDate,
        newStock: Infinity,
        baseStock: 0,
        plannedStock: Infinity,
        newAllocatedStock: totalAllocated,
        allocatedStock: totalAllocated,
        allocatableStock: Infinity,
        allocationStrategy: 'unlimited',
        allocations: after,
        isReleased: true,
      },
      ...after.map((a) => ({
        type: 'AllocationRequest',
        date: a.shipOn,
        quantityRequested: a.quantity,
        quantityAllocated: a.quantity,
        quantityConstrained: 0,
        pageItemId: a.pageItemId,
        allocation: a,
      })),
    ];
  }

  return [
    {
      type: 'Always',
      date: farPast,
      newStock: Infinity,
      baseStock: 0,
      plannedStock: Infinity,
      newAllocatedStock: totalAllocated,
      allocatedStock: totalAllocated,
      allocatableStock: Infinity,
      allocationStrategy: 'unlimited',
      allocations,
      isReleased: true,
    },
    ...allocations.map((a) => ({
      type: 'AllocationRequest',
      date: a.shipOn,
      quantityRequested: a.quantity,
      quantityAllocated: a.quantity,
      quantityConstrained: 0,
      pageItemId: a.pageItemId,
      allocation: a,
    })),
  ];
};

type BlockingBaseStockAtDate = (
  balance: StockItemBalance,
  strategy: AllocationStrategy,
) => (date: Date) => number;

export const blockingBaseStockAtDate: BlockingBaseStockAtDate =
  ({ releaseDate, stockShipments }, _strategy) =>
  (date) => {
    if (releaseDate && isBefore(date, releaseDate)) return 0;

    return sum(
      stockShipments
        .filter((ss) => isBeforeOrSame(ss.availableOn, date))
        .map((ss) => ss.quantity),
    );
  };

type MostRecentLedgerRow = (ledger: Ledger) => (date: Date) => LedgerRow | null;

export const mostRecentLedgerRow: MostRecentLedgerRow = (ledger) => (date) =>
  ledger.find((row) => isBeforeOrSame(row.date, date)) || null;

export const stockIncreaseLedgerRows = (
  ledger: Ledger,
): ROA<StockIncreaseLedgerRow> =>
  ledger
    .map((row: LedgerRow): StockIncreaseLedgerRow | null => {
      if (
        row.type === 'StockShipment' ||
        row.type === 'Release' ||
        row.type === 'Always'
      )
        return row;

      return null;
    })
    .filter(Boolean);

export const allocationRequestLedgerRows = (
  ledger: Ledger,
): ROA<AllocationRequestLedgerRow> =>
  ledger
    .map((row: LedgerRow): AllocationRequestLedgerRow | null => {
      if (row.type === 'AllocationRequest') return row;

      return null;
    })
    .filter(Boolean);

export const separatedLedgerRows = (
  ledger: Ledger,
): [ROA<StockIncreaseLedgerRow>, ROA<AllocationRequestLedgerRow>] => [
  stockIncreaseLedgerRows(ledger),
  allocationRequestLedgerRows(ledger),
];

export const ledgerInDateOrder = (ledger: Ledger): Ledger =>
  orderBy([(s) => s.date], 'asc', ledger);

export const updatedStockIncreaseLedgerRow = (
  { type, ..._baseRowFields }: StockIncreaseLedgerRow,
  fields: StockIncreaseLedgerRowCommonFields,
): StockIncreaseLedgerRow => {
  switch (type) {
    case 'StockShipment':
      return {
        type: 'StockShipment',
        ...fields,
      };
    case 'Always':
      return {
        type: 'Always',
        ...fields,
      };
    case 'Release':
      return {
        type: 'Release',
        ...fields,
      };

    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (type: empty);
      throw new Error(`Unhandled LedgerRow type: ${type}`);
  }
};

export const stockIncreaseLedgerRow = (
  type: $PropertyType<StockIncreaseLedgerRow, 'type'>,
  fields: StockIncreaseLedgerRowCommonFields,
): StockIncreaseLedgerRow => {
  switch (type) {
    case 'StockShipment':
      return {
        type: 'StockShipment',
        ...fields,
      };
    case 'Always':
      return {
        type: 'Always',
        ...fields,
      };
    case 'Release':
      return {
        type: 'Release',
        ...fields,
      };

    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (type: empty);
      throw new Error(`Unhandled LedgerRow type: ${type}`);
  }
};

export const stockIncreasesMinusAllocation = (
  stockIncreases: $ReadOnlyArray<StockIncreaseLedgerRow>,
  allocation: Allocation,
  quantity?: number,
  date?: Date | null, // `null` means don't restrict by date, `void` means unspecified
): $ReadOnly<{|
  remainder: number,
  rows: $ReadOnlyArray<StockIncreaseLedgerRow>,
|}> =>
  stockIncreases.reduce(
    ({ remainder, rows }, baseRow) => {
      // if this stock increase occurs after the allocation date, it can't possibly be
      // relevant
      if (date !== null && isBefore(date || allocation.shipOn, baseRow.date)) {
        return {
          remainder,
          rows: [...rows, baseRow],
        };
      }

      // it's important to keep track of what stock increase event we're allocating stock
      // from
      const toAllocate = Math.min(
        remainder,
        baseRow.newStock,
        baseRow.plannedStock,
      );

      // eslint-disable-next-line no-unused-vars
      const { type: _rowType, ...fields } = baseRow;

      const newRowFields: StockIncreaseLedgerRowCommonFields = {
        ...fields,
        allocatableStock: baseRow.allocatableStock - toAllocate,
        newAllocatedStock: baseRow.newAllocatedStock + toAllocate,
        plannedStock: baseRow.plannedStock - toAllocate,
        allocations: [
          ...baseRow.allocations,
          ...(toAllocate > 0 ? [allocation] : []),
        ],
      };

      return {
        remainder: remainder - toAllocate,
        rows: [...rows, updatedStockIncreaseLedgerRow(baseRow, newRowFields)],
      };
    },
    ({
      remainder: typeof quantity === 'number' ? quantity : allocation.quantity,
      rows: [],
    }: $ReadOnly<{|
      remainder: number,
      rows: $ReadOnlyArray<StockIncreaseLedgerRow>,
    |}>),
  );

export const resolvedLedgerForBlockingAllocation = (
  ledger: Ledger,
  allocation: Allocation,
): Ledger => {
  const [stockIncreases, allocationRequests] = separatedLedgerRows(ledger);

  const { remainder, rows: adjustedRows } = stockIncreasesMinusAllocation(
    // we need to allocate from these rows in last-in-first-out order, and they're currently
    // in ascending date order, so we're gonna reverse them
    reverse(stockIncreases),
    allocation,
  );

  return ledgerInDateOrder([
    // we need to recalculate some of this stuff in forward-order now to bring everything
    // up to date with our new allocations
    ...reverse(adjustedRows).map((row, idx, list) => {
      const lastRow = list[idx - 1];

      // eslint-disable-next-line no-unused-vars
      const { type: _rowType, ...fields } = row;

      return updatedStockIncreaseLedgerRow(row, {
        ...fields,
        plannedStock:
          (lastRow?.plannedStock || 0) + row.newStock - row.newAllocatedStock,
        allocatedStock: sum(
          list.slice(0, idx + 1).map((r) => r.newAllocatedStock),
        ),
        allocatableStock:
          (lastRow?.plannedStock || 0) + row.newStock - row.newAllocatedStock,
      });
    }),
    // we took all our allocation requests out at the beginning, now we need to put them
    // back in...
    ...allocationRequests,
    // ...including our new one
    {
      type: 'AllocationRequest',
      date: allocation.shipOn,
      pageItemId: allocation.pageItemId,
      quantityRequested: allocation.quantity,
      quantityAllocated: allocation.quantity - remainder,
      quantityConstrained: remainder,
      allocation,
    },
  ]);
};

export const resolvedLedgerForBackorderAllocation = (
  ledger: Ledger,
  allocation: Allocation,
): Ledger => {
  const blocking = resolvedLedgerForBlockingAllocation(ledger, allocation);

  const [stockIncreases, allocationRequests] = separatedLedgerRows(blocking);

  const allocationRequest = allocationRequests.find(
    (row) => row.type === 'AllocationRequest' && row.allocation === allocation,
  );

  invariant(
    allocationRequest,
    'Must be able to find the allocation we literally just inserted.',
  );

  const constrained = allocationRequest.quantityConstrained;

  const { remainder, rows: adjustedRows } = stockIncreasesMinusAllocation(
    stockIncreases,
    allocation,
    constrained,
    null,
  );

  const lastStockIncrease = last(adjustedRows);

  const allocatable = lastStockIncrease.allocatableStock;

  return ledgerInDateOrder([
    // we need to recalculate some of this stuff in forward-order now to bring everything
    // up to date with our new allocations
    ...adjustedRows.map((row, idx, list) => {
      const lastRow = list[idx - 1];

      // eslint-disable-next-line no-unused-vars
      const { type: _rowType, ...fields } = row;

      return updatedStockIncreaseLedgerRow(row, {
        ...fields,
        plannedStock:
          (lastRow?.plannedStock || 0) + row.newStock - row.newAllocatedStock,
        allocatedStock:
          (lastRow?.newAllocatedStock || 0) + row.newAllocatedStock,
        allocatableStock: allocatable,
      });
    }),
    // we took all our allocation requests out at the beginning, now we need to put them
    // back in...
    ...allocationRequests.filter((a) => a !== allocationRequest),
    // ...including our new one
    {
      type: 'AllocationRequest',
      date: allocation.shipOn,
      pageItemId: allocation.pageItemId,
      quantityRequested: allocation.quantity,
      quantityAllocated: allocation.quantity - remainder,
      quantityConstrained: remainder,
      allocation,
    },
  ]);
};

type BaseLedgerRowForDate = (
  balance: StockItemBalance,
  strategy: AllocationStrategy,
  contextLine: StockIncreaseLedgerRow,
) => (date: Date) => StockIncreaseLedgerRow;

export const baseLedgerRowForDate: BaseLedgerRowForDate =
  (balance, strategy, contextLine) => (date) => {
    const baseStock = blockingBaseStockAtDate(balance, strategy)(date);

    return stockIncreaseLedgerRow(
      balance.releaseDate && isSameDay(balance.releaseDate, date)
        ? 'Release'
        : 'StockShipment',
      {
        date,
        newStock: baseStock - (contextLine?.plannedStock || 0),
        baseStock,
        plannedStock: baseStock,
        newAllocatedStock: 0,
        allocatedStock: 0,
        allocatableStock: baseStock,
        allocationStrategy: resolvedStrategyAtDate(strategy)(date),
        allocations: [],
        isReleased:
          !balance.releaseDate || isAfterOrSame(date, balance.releaseDate),
      },
    );
  };

export const blockingBaseLedger = (
  balance: StockItemBalance,
  strategy: AllocationStrategy,
): $ReadOnlyArray<StockIncreaseLedgerRow> =>
  blockingRelevantDates(balance, strategy).reduce(
    (acc, date) => [
      ...acc,
      baseLedgerRowForDate(balance, strategy, last(acc))(date),
    ],
    ([]: $ReadOnlyArray<StockIncreaseLedgerRow>),
  );

export const backorderBaseLedger = (
  balance: StockItemBalance,
  strategy: AllocationStrategy,
): Ledger => {
  const blocking = blockingBaseLedger(balance, strategy);

  const lastRow = last(blocking);

  if (!lastRow) return blocking;

  const { allocatableStock } = lastRow;

  return blocking.map((row) => {
    // eslint-disable-next-line no-unused-vars
    const { type: _rowType, ...fields } = row;
    return updatedStockIncreaseLedgerRow(row, {
      ...fields,
      allocatableStock,
    });
  });
};

export const dateWindowBaseLedger = (
  balance: StockItemBalance,
  strategy: DateWindowStrategy,
): $ReadOnlyArray<StockIncreaseLedgerRow> =>
  dateWindowRelevantDates(balance, strategy).reduce(
    (acc, date) => [
      ...acc,
      baseLedgerRowForDate(balance, strategy, last(acc))(date),
    ],
    ([]: $ReadOnlyArray<StockIncreaseLedgerRow>),
  );

export const unrestrictedLedger = (
  balance: StockItemBalance,
  strategy: AllocationStrategy,
): Ledger => {
  const blocking = blockingBaseLedger(balance, strategy);

  const { releaseDate, allocations } = balance;

  const [before, after] = releaseDate
    ? partition((a) => isBefore(a.shipOn, releaseDate))(allocations)
    : [[], allocations];

  return ledgerInDateOrder([
    ...before.map((a) => ({
      type: 'AllocationRequest',
      date: a.shipOn,
      quantityRequested: a.quantity,
      quantityAllocated: 0,
      quantityConstrained: a.quantity,
      pageItemId: a.pageItemId,
      allocation: a,
    })),
    ...blocking,
    ...after.map((a) => ({
      type: 'AllocationRequest',
      date: a.shipOn,
      quantityRequested: a.quantity,
      quantityAllocated: a.quantity,
      quantityConstrained: 0,
      pageItemId: a.pageItemId,
      allocation: a,
    })),
  ]);
};

export const blockingLedger = (
  balance: StockItemBalance,
  strategy: AllocationStrategy,
): Ledger =>
  balance.allocations.reduce(
    resolvedLedgerForBlockingAllocation,
    blockingBaseLedger(balance, strategy),
  );

export const backorderLedger = (
  balance: StockItemBalance,
  strategy: AllocationStrategy,
): Ledger =>
  balance.allocations.reduce(
    resolvedLedgerForBackorderAllocation,
    backorderBaseLedger(balance, strategy),
  );

export const dateWindowLedger = (
  balance: StockItemBalance,
  strategy: DateWindowStrategy,
): Ledger =>
  balance.allocations.reduce(
    resolvedLedgerForBlockingAllocation,
    dateWindowBaseLedger(balance, strategy),
  );

export const ledger = (balance: StockItemBalance): Ledger => {
  const allocationStrategy = balance.strategy;

  switch (allocationStrategy.type) {
    case 'unlimited':
      return unlimitedLedger(balance, allocationStrategy);
    case 'unrestricted':
      return unrestrictedLedger(balance, allocationStrategy);
    case 'blocking':
      return blockingLedger(balance, allocationStrategy);
    case 'backorder':
      return backorderLedger(balance, allocationStrategy);
    case 'shipmentsByAvailableStock':
      return backorderLedger(balance, allocationStrategy);
    case 'dateWindow':
      return dateWindowLedger(balance, allocationStrategy);
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (allocationStrategy.type: empty);
      throw new Error(
        `Unhandled AllocationStrategy type: ${allocationStrategy.type}`,
      );
  }
};

/**
 * Binary operation combining two `StockItemBalance`s into one.
 */
export const stockItemBalancesMerged = (
  a: StockItemBalance,
  b: StockItemBalance,
): StockItemBalance => {
  const mergedBalance: StockItemBalance = {
    id: makeId(),
    strategy: highestPriorityAllocationStrategy([a.strategy, b.strategy]),
    allocations: [...a.allocations, ...b.allocations],
    stockShipments: stockShipmentsInDateOrder([
      ...a.stockShipments,
      ...b.stockShipments,
    ]),
    constrainedAllocations: [
      ...a.constrainedAllocations,
      ...b.constrainedAllocations,
    ],
    unallocatedStockByDate: stockShipmentsInDateOrder([
      ...a.unallocatedStockByDate,
      ...b.unallocatedStockByDate,
    ]),
    releaseDate: latest([a.releaseDate, b.releaseDate].filter(Boolean)),
    ledger: [],
  };

  return {
    ...mergedBalance,
    ledger: ledger(mergedBalance),
  };
};

export const stockItemBalanceListMerged = (
  list: $ReadOnlyArray<StockItemBalance>,
): StockItemBalance =>
  list.reduce(stockItemBalancesMerged, {
    id: makeId(),
    strategy: { type: 'unlimited' },
    stockShipments: [],
    allocations: [],
    constrainedAllocations: [],
    unallocatedStockByDate: [],
    releaseDate: null,
    ledger: [],
  });

/**
 * The given `StockItemBalance` with new `allocations` and `stockShipments` applied,
 * accounting for all allocation rules.
 */
export const resolvedBalance = (
  allocations: $ReadOnlyArray<Allocation>,
  stockShipments: StockShipments,
  balance: StockItemBalance,
): StockItemBalance => ({
  ...allocations.reduce(
    resolvedBalanceForAllocation,
    ({
      ...balance,
      unallocatedStockByDate: stockShipmentsInDateOrder(stockShipments),
      stockShipments,
      constrainedAllocations: [],
      allocations: [],
    }: StockItemBalance),
  ),
  id: makeId(),
});

// Query functions
//
// These functions are used to query the `Inventory` about current state.

/**
 * The balance for the given stock item in the given warehouse.
 */

export const balanceForStockItemKey =
  (
    inventory: Inventory,
  ): ((warehouseId: WarehouseId, stockItemKey: string) => StockItemBalance) =>
  (warehouseId: WarehouseId, stockItemKey: string): StockItemBalance => {
    const balances = inventory.get(stockItemKey);
    invariant(
      balances,
      `An inventory entry must exist for ${stockItemKey} when looking it up.`,
    );

    const balance = balances[warehouseId] || emptyStockItemBalance;

    return balance;
  };

/**
 * `StockItemBalance`s for the given stock item key across all warehouses by warehouse.
 */
export const stockItemKeyBalancesByWarehouse =
  (inventory: Inventory): ((stockItemKey: string) => BalancesByWarehouse) =>
  (stockItemKey: string): BalancesByWarehouse => {
    const entry = inventory.get(stockItemKey);

    invariant(
      entry,
      `Entry must exist for ${stockItemKey} when looking it up in inventory.`,
    );

    return entry;
  };

/**
 * `StockItemBalance`s for the given stock item key across all warehouses.
 */
export const stockItemKeyBalances =
  (
    stockItemKey: string,
  ): ((inventory: Inventory) => $ReadOnlyArray<StockItemBalance>) =>
  (inventory: Inventory): $ReadOnlyArray<StockItemBalance> =>
    mapWarehouses((balance) => balance)(
      stockItemKeyBalancesByWarehouse(inventory)(stockItemKey),
    );

/**
 * `WarehouseId`s that the given stock item key appears in.
 */
export const stockItemKeyWarehouseIds =
  (
    stockItemKey: string,
  ): ((inventory: Inventory) => $ReadOnlyArray<WarehouseId>) =>
  (inventory: Inventory): $ReadOnlyArray<WarehouseId> =>
    Object.keys(stockItemKeyBalancesByWarehouse(inventory)(stockItemKey));

/**
 * The amount of unallocated stock for the given stock item key at the given date.
 *
 * Specify `excludePageItemIds` to exclude allocations for the given `excludePageItemIds`
 * from stock calculations.
 */
export const unallocatedStockForStockItemKeyAtDate =
  (
    inventory: Inventory,
  ): ((
    warehouseId: WarehouseId,
    stockItemKey: string,
    date: Date,
    excludePageItemIds?: $ReadOnlyArray<string>,
  ) => number) =>
  (
    warehouseId: WarehouseId,
    stockItemKey: string,
    date: Date,
    excludePageItemIds?: $ReadOnlyArray<string>,
  ): number =>
    unallocatedStockAtDate(
      date,
      excludePageItemIds,
    )(balanceForStockItemKey(inventory)(warehouseId, stockItemKey));

/**
 * `AllocationStrategy` for the given stock item key.
 */
export const allocationStrategyForStockItemKey =
  (
    inventory: Inventory,
  ): ((warehouseId: WarehouseId, stockItemKey: string) => AllocationStrategy) =>
  (warehouseId: WarehouseId, stockItemKey: string): AllocationStrategy =>
    balanceForStockItemKey(inventory)(warehouseId, stockItemKey).strategy;

/**
 * `ResolvedAllocationStrategy` for the given stock item key at the given `Date`.
 */
export const allocationStrategyForStockItemKeyAtDate =
  (
    inventory: Inventory,
  ): ((stockItemKey: string, date: Date) => ResolvedAllocationStrategy) =>
  (stockItemKey: string, date: Date): ResolvedAllocationStrategy =>
    resolvedStrategyAtDate(
      values(stockItemKeyBalancesByWarehouse(inventory)(stockItemKey))[0]
        .strategy,
    )(date);

/**
 * The amount of unallocated stock for the given stock item key at each stock shipment date.
 *
 * Specify `excludePageItemIds` to exclude allocations for the given `excludePageItemIds`
 * from stock calculations.
 */
export const unallocatedStockForStockItemKey =
  (
    inventory: Inventory,
  ): ((
    warehouseId: WarehouseId,
    stockItemKey: string,
    excludePageItemIds?: $ReadOnlyArray<string>,
  ) => $ReadOnlyArray<StockShipment>) =>
  (
    warehouseId: WarehouseId,
    stockItemKey: string,
    excludePageItemIds?: $ReadOnlyArray<string>,
  ): $ReadOnlyArray<StockShipment> =>
    balanceForStockItemKey(inventory)(
      warehouseId,
      stockItemKey,
    ).unallocatedStockByDate.map(({ availableOn }) => ({
      availableOn,
      quantity: unallocatedStockForStockItemKeyAtDate(inventory)(
        warehouseId,
        stockItemKey,
        availableOn,
        excludePageItemIds,
      ),
    }));

/**
 * The constrained allocations for the given stock item key in the given order PageItem.
 */
export const constrainedAllocationsForStockItemKeyInWarehouse =
  (
    warehouseId: WarehouseId,
    stockItemKey: string,
    excludePageItemIds?: $ReadOnlyArray<string> = [],
  ): ((inventory: Inventory) => $ReadOnlyArray<ConstrainedAllocation>) =>
  (inventory: Inventory): $ReadOnlyArray<ConstrainedAllocation> =>
    balanceForStockItemKey(inventory)(
      warehouseId,
      stockItemKey,
    ).constrainedAllocations.filter((a) =>
      excludePageItemIds.includes(a.pageItemId),
    );

export const constrainedAllocationsForStockItemKey =
  (
    stockItemKey: string,
  ): ((inventory: Inventory) => $ReadOnlyArray<ConstrainedAllocationRecord>) =>
  (inventory: Inventory): $ReadOnlyArray<ConstrainedAllocationRecord> =>
    mapWarehouses((balance, warehouseId) => ({ warehouseId, balance }))(
      stockItemKeyBalancesByWarehouse(inventory)(stockItemKey),
    ).flatMap(({ balance, warehouseId }) =>
      balance.constrainedAllocations.map((constrainedAllocation) => ({
        ...constrainedAllocation,
        stockItemKey,
        warehouseId,
      })),
    );

export const asBlockingBalance: (
  StockItemBalance,
  excludePageItemIds?: $ReadOnlyArray<string>,
) => StockItemBalance = (balance, excludePageItemIds = []) =>
  resolvedBalance(
    balance.allocations.filter(
      (a) => !excludePageItemIds.includes(a.pageItemId),
    ),
    balance.stockShipments,
    {
      ...balance,
      strategy: { type: 'blocking' },
    },
  );

export const placeholderAllocationsForStockItemBalance: (
  StockItemBalance,
  excludePageItemIds?: $ReadOnlyArray<string>,
) => $ReadOnlyArray<ConstrainedAllocation> = (
  balance,
  excludePageItemIds = [],
) =>
  balance.strategy.type === 'shipmentsByAvailableStock'
    ? asBlockingBalance(balance, excludePageItemIds).constrainedAllocations
    : [];

export const placeholderAllocationsForStockItemKeyInWarehouse: (
  warehouseId: WarehouseId,
  stockItemKey: string,
  excludePageItemIds?: $ReadOnlyArray<string>,
) => (Inventory) => $ReadOnlyArray<ConstrainedAllocation> =
  (warehouseId, stockItemKey, excludePageItemIds = []) =>
  (inventory) =>
    placeholderAllocationsForStockItemBalance(
      balanceForStockItemKey(inventory)(warehouseId, stockItemKey),
      excludePageItemIds,
    );

export const placeholderAllocationsForStockItemKey: (
  stockItemKey: string,
  excludePageItemIds?: $ReadOnlyArray<string>,
) => (Inventory) => $ReadOnlyArray<ConstrainedAllocationRecord> =
  (stockItemKey, excludePageItemIds = []) =>
  (inventory) =>
    mapWarehouses((balance, warehouseId) =>
      placeholderAllocationsForStockItemBalance(
        balance,
        excludePageItemIds,
      ).map((constrainedAllocation) => ({
        ...constrainedAllocation,
        stockItemKey,
        warehouseId,
      })),
    )(stockItemKeyBalancesByWarehouse(inventory)(stockItemKey)).flat();

// Update functions
//
// These functions are used to update an `Inventory` with new state.

/**
 * The given inventory with the given incoming allocations merged into it, and
 * all balances resolved.
 *
 * This accounts for allocation rules, so it may result in constrained allocations
 * or unlimited allocations as the case may be.
 */
export const mergedAllocations = (
  inventory: Inventory,
  incomingAllocations: $ReadOnlyArray<AllocationRecord>,
): Inventory => {
  const byKey = groupBy((a) => a.stockItemKey, incomingAllocations);
  return inventory.merge(
    immutableMap(
      Object.keys(byKey).map((stockItemKey) => {
        const balances = inventory.get(stockItemKey);

        invariant(
          balances,
          `Must have an inventory entry for ${stockItemKey} when attempting to allocate.`,
        );

        const allocationsByWarehouse = groupBy(
          (a) => a.warehouseId,
          byKey[stockItemKey],
        );

        return [
          stockItemKey,
          fromPairs(
            mapWarehouses((balance, warehouseId) => {
              const allocationRecords = allocationsByWarehouse[warehouseId];
              const allocationPageItemIds = (allocationRecords || []).map(
                (r) => r.pageItemId,
              );
              const resolveStockItemBalance = allocationRecords
                ? resolvedBalance(
                    [
                      ...balance.allocations.filter(
                        (a) => !allocationPageItemIds.includes(a.pageItemId),
                      ),
                      ...allocationRecords.map(
                        ({ quantity, shipOn, pageItemId }) => ({
                          quantity,
                          shipOn,
                          pageItemId,
                        }),
                      ),
                    ],
                    balance.stockShipments,
                    balance,
                  )
                : balance;

              return [
                warehouseId,
                {
                  ...resolveStockItemBalance,
                  ledger: ledger(resolveStockItemBalance),
                },
              ];
            })(balances),
          ),
        ];
      }),
    ),
  );
};

/**
 * The given inventory with the given incoming stock shipments merged into it, and
 * all balances resolved.
 *
 * This accounts for allocation rules, so it may result in constrained allocations
 * or unlimited allocations as the case may be.
 */
export const mergedStock = (
  inventory: Inventory,
  incomingStockShipments: $ReadOnlyArray<StockShipmentsRecord>,
): Inventory => {
  const byKey = groupBy((a) => a.stockItemKey, incomingStockShipments);
  return inventory.merge(
    immutableMap(
      Object.keys(byKey).map((stockItemKey) => {
        const balances = inventory.get(stockItemKey);

        const stockShipmentsRecordsByWarehouse = groupBy(
          (a) => a.warehouseId,
          byKey[stockItemKey],
        );

        const oldBalances: BalancesByWarehouse = balances || { ...null };

        const newBalances: BalancesByWarehouse = fromPairs(
          Object.keys(stockShipmentsRecordsByWarehouse)
            .map((warehousId) => [
              warehousId,
              stockShipmentsRecordsByWarehouse[warehousId],
            ])
            .map(([warehouseId, stockShipmentsRecords]) => {
              const stockShipmentsRecord = stockShipmentsRecords?.[0];
              const balance = balances?.[warehouseId];

              return [
                warehouseId,
                stockShipmentsRecord
                  ? resolvedBalance(
                      balance?.allocations || [],
                      stockShipmentsRecord.stock.stockShipments,
                      {
                        ...(balance || emptyStockItemBalance),
                        strategy: stockShipmentsRecord.stock.strategy,
                      },
                    )
                  : balance || {
                      ...emptyStockItemBalance,
                      id: makeId(),
                    },
              ];
            })
            .map(([warehouseId, stockItemBalance]) => [
              warehouseId,
              {
                ...stockItemBalance,
                ledger: ledger(stockItemBalance),
              },
            ]),
        );

        const combinedBalances: BalancesByWarehouse = {
          ...oldBalances,
          ...newBalances,
        };

        return [stockItemKey, combinedBalances];
      }),
    ),
  );
};

export const stockItemBalanceWithoutAllocation =
  (
    pageItemIds: $ReadOnlyArray<string>,
  ): ((stockItemBalance: StockItemBalance) => StockItemBalance) =>
  (stockItemBalance: StockItemBalance): StockItemBalance =>
    resolvedBalance(
      stockItemBalance.allocations.filter(
        (a) => !pageItemIds.includes(a.pageItemId),
      ),
      stockItemBalance.stockShipments,
      stockItemBalance,
    );

export const withoutStockItemKeyAllocations =
  (
    stockItemKey: string,
    pageItemIds: $ReadOnlyArray<string>,
  ): ((inventory: Inventory) => Inventory) =>
  (inventory: Inventory): Inventory => {
    const adjustedBalances = fromPairs(
      mapWarehouses((balance, warehouseId) => [
        warehouseId,
        stockItemBalanceWithoutAllocation(pageItemIds)(balance),
      ])(stockItemKeyBalancesByWarehouse(inventory)(stockItemKey)),
    );

    return inventory.set(stockItemKey, adjustedBalances);
  };

export const fromObject: ({
  +[stockItemKey: string]: BalancesByWarehouse,
}) => Inventory = immutableMap;

export const emptyInventory: (void) => Inventory = immutableMap;

export const stockItemBalanceRelevantDates = (
  balance: StockItemBalance,
): $ReadOnlyArray<Date> =>
  earliestToLatest(
    uniqBy(
      (date) => date && unixTimeMs(date),
      [
        ...balance.stockShipments.map(({ availableOn }) => availableOn),
        allocationStrategyFirstUnlimitedDate(balance.strategy),
      ].filter(Boolean),
    ),
  );

export const firstDateWithMinimumPlannedAllocatableStock = (
  balance: StockItemBalance,
  minimum: number,
  excludePageItemIds: $ReadOnlyArray<string> | null,
): Date | null =>
  stockItemBalanceRelevantDates(balance).find(
    (date) =>
      plannedAllocatableStockAtDate(date, excludePageItemIds || [])(balance) >=
      minimum,
  ) || null;

export const firstDateAllPlaceholderAllocationsAreAllocatable: (
  StockItemBalance,
  pageItemIds: $ReadOnlyArray<string>,
  excludePageItemIds?: $ReadOnlyArray<string>,
) => Date | null = (balance, pageItemIds, excludePageItemIds = []) =>
  firstDateWithMinimumPlannedAllocatableStock(
    asBlockingBalance(balance, excludePageItemIds),
    sum(
      placeholderAllocationsForStockItemBalance(balance, excludePageItemIds)
        .filter((a) => pageItemIds.includes(a.pageItemId))
        .map((allocation) => allocation.quantity),
    ),
    excludePageItemIds,
  );

export const earliestAllocationsWithPlaceholdersResolved: (
  StockItemBalance,
  pageItemIds: $ReadOnlyArray<string>,
  excludePageItemIds: $ReadOnlyArray<string>,
) => $ReadOnlyArray<Allocation> = (
  balance,
  pageItemIds,
  excludePageItemIds = [],
) => {
  if (balance.strategy.type !== 'shipmentsByAvailableStock') return [];

  const blockingBalance = asBlockingBalance(balance, excludePageItemIds);

  const placeholderAllocations = placeholderAllocationsForStockItemBalance(
    balance,
    excludePageItemIds,
  ).filter((a) => pageItemIds.includes(a.pageItemId));

  const dates = earliestToLatest(
    balance.stockShipments.map((ss) => ss.availableOn),
  );

  return placeholderAllocations.reduce(
    (
      [accBalance, accAllocations],
      placeholderAllocation,
    ): [StockItemBalance, $ReadOnlyArray<Allocation>] => {
      const [balanceAfterAllocation, remainder, allocations] = dates.reduce(
        (
          [innerAccBalance, remaining, innerAllocations]: [
            StockItemBalance,
            number,
            $ReadOnlyArray<Allocation>,
          ],
          date,
        ): [StockItemBalance, number, $ReadOnlyArray<Allocation>] => {
          if (remaining <= 0)
            return [innerAccBalance, remaining, innerAllocations];

          const stock = unallocatedStockAtDate(
            date,
            excludePageItemIds,
          )(innerAccBalance);

          if (stock === 0)
            return [innerAccBalance, remaining, innerAllocations];

          const toAllocate = Math.min(stock, remaining);

          const allocation = {
            quantity: toAllocate,
            shipOn: date,
            pageItemId: placeholderAllocation.pageItemId,
          };

          return [
            resolvedBalance(
              [...innerAccBalance.allocations, allocation],
              innerAccBalance.stockShipments,
              innerAccBalance,
            ),
            remaining - toAllocate,
            [...innerAllocations, allocation],
          ];
        },
        [accBalance, placeholderAllocation.quantity, []],
      );

      invariant(
        remainder <= 0,
        'There must never be a remainder in allocationsForPlaceholderAllocations',
      );

      if (allocations.length === 0) return [accBalance, accAllocations];

      return [balanceAfterAllocation, [...accAllocations, ...allocations]];
    },
    [blockingBalance, []],
  )[1];
};

export const placeholderAllocations = (
  allocations: $ReadOnlyArray<ConstrainedAllocationRecord>,
  pageProducts: Array<PageProduct>,
): number =>
  sum(
    allocations
      .filter((a) =>
        pageProducts
          .flatMap((pp) => pp.pageItems)
          .map((pi) => pi.uniqueId)
          .includes(a.pageItemId),
      )
      .map((a) => a.quantity) || [],
  );

export const orderPlaceholderAllocations = (
  allocationsForOrder: $ReadOnlyArray<
    $ReadOnlyArray<ConstrainedAllocationRecord>,
  >,
  order: Order,
): number =>
  sum(
    allocationsForOrder.map((allocations, indx) =>
      placeholderAllocations(allocations, order.pages[indx].pageProducts),
    ),
  );

export const ledgerRolledUpToDate =
  (date: Date): ((ledgerToBeRolledUp: Ledger) => Ledger) =>
  (ledgerToBeRolledUp: Ledger): Ledger =>
    stockIncreaseLedgerRows(ledgerToBeRolledUp).reduce(
      (prev: Ledger, curr: LedgerRow) =>
        isBeforeOrSame(curr.date, date) ? [{ ...curr, date }] : [...prev, curr],
      [],
    );

export const distinctSizeNames = ({
  variation,
  order = 'default',
}: $ReadOnly<{|
  variation: Variation,
  order?: 'default' | 'interleavedByAltName',
|}>): $ReadOnlyArray<string> => {
  switch (order) {
    case 'default':
      return defaultSizeNames(variation);
    case 'interleavedByAltName':
      return distinctSizeNamesByPosition(variation);
    default:
      // eslint-disable-next-line babel/no-unused-expressions
      (order: empty);
      throw new Error(`Unhandled order: ${order}`);
  }
};

export const quantityCalculations = ({
  quantityTableSizeSort,
  pageProduct,
  pageItems,
  stockItems,
  customer,
}: $ReadOnly<{|
  quantityTableSizeSort?: 'default' | 'interleavedByAltName',
  pageProduct: PageProduct,
  pageItems: $ReadOnlyArray<PageItem>,
  stockItems: $ReadOnlyArray<StockItemInWarehouseAtDate | StockItemAtDate>,
  customer?: Customer,
|}>): SizeAndAltNames => {
  const relevantSizeNames = uniq(
    pageItems.map((pi) => pi.stockItem).map((si) => si.name),
  );

  const relevantAltNames = uniq(
    compact(pageItems.flatMap((pi) => pi.stockItem).map((si) => si.alt_name)),
  );

  const sizeNames = intersection(
    relevantSizeNames,
    distinctSizeNames({
      variation: pageProduct.variation,
      order: quantityTableSizeSort,
    }),
  );

  const altNames = intersection(
    relevantAltNames,
    distinctAlternateNames(pageProduct.variation),
  );

  if (customer?.b2c && !pageProduct.readOnly) {
    const sizeNamesQuantity = sizeNames.filter((sizeName) =>
      stockItems
        .filter((item) => item.stockItem.name === sizeName)
        .some((si) => si.plannedAllocatableStock > 0),
    );

    const altNamesQuantity = altNames.filter((altName) =>
      stockItems
        .filter((item) => item.stockItem.alt_name === altName)
        .some((si) => si.plannedAllocatableStock > 0),
    );
    return { sizeNames: sizeNamesQuantity, altNames: altNamesQuantity };
  }
  return { sizeNames, altNames };
};
